<template>
  <div v-if="customer" class="py-6">
    <div class="flex justify-between items-center space-x-8 mb-8 border-b pb-4">
      <div>
        <h4 class="text-lg mr-4 leading-none font-semibold ">
          {{ $t('general.passes') }}
        </h4>
      </div>

      <div class="flex items-center space-x-2 flex-shrink-0">
        <o-button variant="primary" flat @click="handleAddCustomerPass">
          {{ $t('pass.add') }}
        </o-button>
      </div>
    </div>

    <o-pager
      :button="$t('general.loadMore')"
      :empty="$t('components.customer.profile.passes.empty')"
      :list="customerPasses"
      class="mt-4"
    >
      <template #default="{data}">
        <div v-if="activePasses(data).length">
          <h6 class="text-sm font-semibold mb-2 opacity-70">
            Active
          </h6>
          <div class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center h-full bg-white shadow-lg rounded-2xl mb-6 dark:bg-gray-800/20 dark:border dark:border-gray-800">
            <pass
              v-for="customerPass in activePasses(data)"
              :key="customerPass.idpass"
              :customerPass="customerPass"
              class="w-full flex justify-between items-center flex-grow p-4 border-b border-gray-200 dark:border-gray-800 last:border-none"
              @email="handleEmail(customerPass)"
            />
          </div>
        </div>

        <div v-if="inactivePasses(data).length">
          <h6 class="text-sm font-semibold mb-2 opacity-70">
            Inactive
          </h6>
          <div class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center h-full bg-white shadow-lg rounded-2xl mb-4 dark:bg-gray-800/20 dark:border dark:border-gray-800">
            <pass
              v-for="customerPass in inactivePasses(data)"
              :key="customerPass.idpass"
              :customerPass="customerPass"
              class="w-full flex justify-between items-center flex-grow p-4 border-b border-gray-200 dark:border-gray-800 last:border-none"
              @email="handleEmail(customerPass)"
            />
          </div>
        </div>
      </template>
    </o-pager>

    <div v-if="canViewCourses" class="flex justify-between items-center space-x-8 mb-8 border-b pb-4">
      <div>
        <h4 class="text-lg mr-4 leading-none font-semibold ">
          Courses
        </h4>
      </div>

      <div v-if="canEnrolCourses" class="flex items-center space-x-2 flex-shrink-0">
        <o-panel-link
          tag="o-button"
          variant="primary"
          flat
          :path="`/customer/course/add?idCustomer=${customer.idCustomer}`"
        >
          Enrol in course
        </o-panel-link>
      </div>
    </div>

    <o-pager
      v-if="canViewCourses"
      button="Load more"
      empty="No courses to show..."
      :list="customerCourses"
      class="mt-4"
    >
      <template #default="{data}">
        <div v-if="upcomingCourses(data).length">
          <h6 class="text-sm font-semibold mb-2 opacity-70">
            Upcoming &amp; active
          </h6>
          <div class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center h-full bg-white shadow-lg rounded-2xl mb-6 dark:bg-gray-800/20 dark:border dark:border-gray-800">
            <course
              v-for="customerCourse in upcomingCourses(data)"
              :key="customerCourse.idCustomerCourse"
              :customerCourse="customerCourse"
              class="w-full flex justify-between items-center flex-grow p-4 border-b border-gray-200 dark:border-gray-800 last:border-none"
            />
          </div>
        </div>

        <div v-if="pastCourses(data).length">
          <h6 class="text-sm font-semibold mb-2 opacity-70">
            Past
          </h6>
          <div class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center h-full bg-white shadow-lg rounded-2xl mb-4 dark:bg-gray-800/20 dark:border dark:border-gray-800">
            <course
              v-for="customerCourse in pastCourses(data)"
              :key="customerCourse.idCustomerCourse"
              :customerCourse="customerCourse"
              class="w-full flex justify-between items-center flex-grow p-4 border-b border-gray-200 dark:border-gray-800 last:border-none"
            />
          </div>
        </div>
      </template>
    </o-pager>

    <add-customer-pass-dialog
      ref="addCustomerPassDialog"
    />

    <email-dialog
      ref="emailDialog"
      @success="fetchPasses"
    />
  </div>
</template>

<script setup lang="ts">
import { EndlessListPager } from '@/_api/lib/pager'

import Pass from './pass.vue'
import Course from './course.vue'
import AddCustomerPassDialog from '@/components/customerPass/actions/Add.vue'
import EmailDialog from '@/components/customerPass/actions/Email.vue'

import { OPager } from '@/components/elements'

interface Props {
  customer?: Record<string, any> | null
}

const props = withDefaults(defineProps<Props>(), {
  customer: null,
})

const { $api, $bus, $date, $gates, $grants } = useNuxtApp()

const customerId = computed(() => props.customer?.idCustomer || props.customer?.id)
const canViewCourses = computed(() => $gates.get('courses') && $grants.all({ course: ['view'] }))
const canEnrolCourses = computed(() => canViewCourses.value && $grants.all({ customerCourse: ['create'] }))

const addCustomerPassDialog = ref<any>()
const emailDialog = ref<any>()

const customerPasses = reactive(new EndlessListPager((page: number) => $api.customer.passes(String(customerId.value), { page }), 20))
const customerCourses = reactive(new EndlessListPager((page: number) => $api.customer.courses(String(customerId.value), { page }), 20))

const fetchPasses = async () => {
  await customerPasses.reload()
}

const handleEmail = (customerPass: any) => {
  emailDialog.value.open(customerPass)
}

const fetchCourses = async () => {
  await customerCourses.reload()
}

const handleAddCustomerPass = () => {
  const customer = props.customer
  addCustomerPassDialog.value.open({ customer })
}

const activePasses = (data: any[] = []) => data.filter(pass => pass.isValid)

const inactivePasses = (data: any[] = []) => data.filter(pass => !pass.isValid)

const isCoursePast = (customerCourse: any) => {
  const dates = customerCourse?.courseRun?.dates || []
  if (!dates.length) { return false }
  const today = Number(($date as any).format(new Date()))
  const lastDate = Math.max(...dates)
  return lastDate < today
}

const upcomingCourses = (data: any[] = []) => data.filter(course => !isCoursePast(course))

const pastCourses = (data: any[] = []) => data.filter(course => isCoursePast(course))

// created
const uid = getCurrentInstance()?.uid
$bus.on.all('CustomerPass', uid, (customerPass: any) => {
  const idCustomer = props.customer?.idCustomer
  const current = idCustomer === (customerPass?.customer?.idCustomer || customerPass?.customer?.id)

  if (current) {
    fetchPasses()
  }
})

onMounted(() => {
  customerPasses.load()
  if (canViewCourses.value) {
    customerCourses.load()
  }
})

onBeforeUnmount(() => {
  $bus.off.all(uid)
})
</script>
